/**
 * Oct 20 (Issue 03A, FD-06 / FD-07) — "Picked Up — Work Incomplete" from the FOH Workboard.
 *
 * The SAME authority as the ticket kebab (mark_picked_up_incomplete_if_quiescent, now with a required reason and
 * an optional, informational expected return date) and the SAME derived consequences the kebab converges on
 * (TicketKebabMenu.handleMarkPickedUpIncomplete): pre-pickup reminders suppressed by POSITIVE scope, the
 * picked-up follow-up and owner escalation created-if-absent and anchored to the canonical departure instant,
 * the board cascade, then the cross-device signal and — for a new pickup only — the timeline event.
 *
 * The server never completes the ticket (status Blocked, completed_at NULL), releases the location, closes active
 * custody and records the reason and date (ticket_incomplete_pickups) in the same transaction.
 *
 * KNOWN DUPLICATION: the consequence block mirrors the kebab's line for line; consolidating both callers onto this
 * module is a follow-up (the kebab's source is pinned by several source-scan tests).
 */
import { supabase } from '@/integrations/supabase/client';
import {
  markPickedUpIncomplete,
  isPickupSettled,
  didPickUp,
  needsPickupReload,
  describePickupRefusal,
} from '@/utils/ticketPickupLifecycle';
import {
  classifyEnqueue, classifySuppression, classifyCascade,
  allConsequencesOk, describeConsequences,
  type PickupConsequences,
} from '@/utils/pickupConsequenceResults';
import { triggerBoardCascade } from '@/utils/cascadeTrigger';
import { logTicketEventToOcis } from '@/utils/ocisIngest';
import { notifyPickupChanged } from '@/utils/pickupConvergence';
import { getShopDayLabel } from '@/utils/shopTime';
import type { PickupActFeedback } from './completedPickupAct';

/** The same positive scope the kebab suppresses (TicketKebabMenu PRE_PICKUP_REMINDER_TYPES). */
const PRE_PICKUP_REMINDER_TYPES = [
  'immediate', '24h', '2h', 'custom', 'appointment', 'paused_followup',
] as const;

export async function recordIncompletePickup(args: {
  ticketId: string;
  reason: string;
  expectedReturnDate: string | null;
  actorUserId: string | null;
  fromStatus: string | null;
  shopTimezone: string;
  /** Oct 20 addendum (L2): the paused sessions the person confirmed ending with the departure. */
  releasePauseIds?: readonly string[];
}): Promise<PickupActFeedback> {
  const { ticketId } = args;
  const pickup = await markPickedUpIncomplete(ticketId, args.reason, args.expectedReturnDate, args.releasePauseIds ?? []);

  if (needsPickupReload(pickup.outcome)) {
    return { settled: false, reload: true, tone: 'error', title: 'Pickup result unconfirmed', description: describePickupRefusal(pickup) };
  }
  if (!isPickupSettled(pickup.outcome)) {
    return { settled: false, reload: false, tone: 'error', title: 'Not ready for pickup', description: describePickupRefusal(pickup) };
  }

  const firstPickup = didPickUp(pickup.outcome);
  const pickupShopId = pickup.shopId ?? null;

  // Derived consequences CONVERGE on any settled state (8G.2C.8), anchored to the server's departure instant.
  let consequences: PickupConsequences | null = null;
  if (pickupShopId && pickup.pickedUpAt) {
    const pickedUpMs = new Date(pickup.pickedUpAt).getTime();
    const suppression = await supabase.functions.invoke('cancel-reminders', {
      body: { ticketId, reason: 'picked_up_incomplete', reminderTypes: PRE_PICKUP_REMINDER_TYPES },
    }).catch(err => { console.warn('[recordIncompletePickup] cancel-reminders error:', err); return { error: err }; });
    const followup = await supabase.functions.invoke('enqueue-reminders', {
      body: { ticketId, shopId: pickupShopId, type: 'picked_up_followup', targetRole: 'advisor', sendAt: new Date(pickedUpMs + 24 * 60 * 60 * 1000).toISOString() },
    }).catch(err => { console.warn('[recordIncompletePickup] enqueue advisor followup error:', err); return { error: err }; });
    const escalation = await supabase.functions.invoke('enqueue-reminders', {
      body: { ticketId, shopId: pickupShopId, type: 'escalation', targetRole: 'owner', sendAt: new Date(pickedUpMs + 5 * 24 * 60 * 60 * 1000).toISOString() },
    }).catch(err => { console.warn('[recordIncompletePickup] enqueue owner escalation error:', err); return { error: err }; });
    const cascade = await triggerBoardCascade(pickupShopId, getShopDayLabel(pickup.pickedUpAt, args.shopTimezone))
      .catch(err => { console.warn('[recordIncompletePickup] cascade error:', err); return { ok: false, error: String(err) }; });
    // Oct 20 addendum (L3): the lanes the withdrawn future days held are re-walked (their capacity is free now).
    // oct20d-sched (Founder Decision 4): ONLY those lanes — the technician whose day was freed, on that day. A walk of
    // the whole board on a future day re-timed every other technician's flexible work there (the field audit's
    // "future-day lane walk"), although nothing on their lanes changed. The server names each lane's technician.
    const pickupDay = getShopDayLabel(pickup.pickedUpAt, args.shopTimezone);
    const withdrawnLanes = new Map<string, { day: string; techId: string }>();
    for (const l of pickup.lanes ?? []) {
      const day = String(l.scheduled_date ?? '').slice(0, 10);
      if (day && day !== pickupDay && l.technician_id) withdrawnLanes.set(`${day}|${l.technician_id}`, { day, techId: l.technician_id });
    }
    for (const { day, techId } of [...withdrawnLanes.values()].sort((a, b) => a.day.localeCompare(b.day))) {
      await triggerBoardCascade(pickupShopId, day, techId)
        .catch(err => { console.warn('[recordIncompletePickup] withdrawn-day cascade error:', err); });
    }
    consequences = {
      suppression: classifySuppression(suppression),
      followup: classifyEnqueue(followup),
      escalation: classifyEnqueue(escalation),
      cascade: classifyCascade(cascade),
    };
  }

  notifyPickupChanged({
    isNewPickup: firstPickup, ticketId, shopId: pickupShopId,
    pickedUpAt: pickup.pickedUpAt, act: 'incomplete',
  });

  // The timeline event projects the singular human act: a retried transport writes no second pickup.
  if (firstPickup && pickupShopId) {
    logTicketEventToOcis({
      ticketId,
      shopId: pickupShopId,
      eventType: 'ticket_status_changed',
      content: 'Vehicle picked up (incomplete)',
      authorUserId: args.actorUserId ?? undefined,
      payload: {
        fromStatus: args.fromStatus, toStatus: 'Blocked', reason: 'picked_up_incomplete', picked_up: true,
        pickup_reason: args.reason, expected_return_date: args.expectedReturnDate,
        released_paused_work: pickup.releasedPausedWork ?? 0,
        withdrawn_future_days: (pickup.withdrawnFutureDays ?? []).map((d) => d.ticket_id),
      },
    }).catch(err => console.error('[recordIncompletePickup] OCIS log error:', err));
  }

  return {
    settled: true,
    reload: false,
    tone: consequences && allConsequencesOk(consequences) ? (firstPickup ? 'success' : 'info') : 'error',
    title: firstPickup ? 'Marked Picked Up — Work Incomplete' : 'Already recorded as picked up',
    description: !consequences
      ? 'Pickup recorded. Follow-up reminders could not be scheduled — reopen the ticket to retry.'
      : describeConsequences(consequences),
  };
}
